<template>
  <View gap="md" direction="column" id="pd-upcoming-showings">
    <View justify="space-between">
      <Text variant="h2">Upcoming Showings</Text>
      <ScrollableHorizontallyControls :scrollable-container="scrollableContainerRef" />
    </View>

    <EmptyState
      v-if="!hasUpcomingShowings"
      icon="calendar"
      color="green"
      title="No upcoming showings yet"
      description="Here you'll be able to see upcoming showings, and we'll notify you about them."
    />

    <ScrollableHorizontallyContainer ref="scrollableContainerRef" class="upcoming-scroll" fade>
      <View class="card-items upcoming-showings-block" gap="md" padding-bottom="md">
        <View
          gap="sm"
          direction="column"
          v-for="upcomingShowing in groupedByDateUpcomingShowings"
          :key="upcomingShowing.date.toString()"
        >
          <View gap="sm">
            <Text variant="h4">{{ useDateFormat(upcomingShowing.date, 'MMM D') }}</Text>
            <Text variant="body-large" color="tertiary">
              {{ useDateFormat(upcomingShowing.date, 'dddd') }}
            </Text>
          </View>

          <View gap="sm" direction="column">
            <ShowingCard
              class="showing"
              v-for="showing in upcomingShowing.showings"
              :key="showing.id"
              :showing="showing"
              :show-status="false"
              :show-showing-requests="false"
            />
          </View>
        </View>
      </View>
    </ScrollableHorizontallyContainer>
  </View>
</template>

<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import {
  EmptyState,
  ScrollableHorizontallyContainer,
  ScrollableHorizontallyControls,
  Text,
  View,
} from '@tallo/design-system';
import { groupShowingByDateAndSort, ShowingCard, ShowingStatus, useShowingsQuery } from '@tallo/investor/showing';

const route = useRoute();
const propertyId = computed(() => route.params.propertyId as string);
const { data } = useShowingsQuery({ propertyId, status: ShowingStatus.CONFIRMED });

const groupedByDateUpcomingShowings = computed(() => groupShowingByDateAndSort(data.value || []));
const scrollableContainerRef = ref();
const hasUpcomingShowings = computed(() => groupedByDateUpcomingShowings.value.length);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.upcoming-scroll {
  // Align the scroll strip with the widgets above it (e.g. Access instructions): cancel the shared
  // container's horizontal "bleed" margin, but keep the vertical bleed so card shadows aren't clipped.
  margin-left: 0;
  margin-right: 0;
}

:deep(.container-inner) {
  // Keep the first card flush with the widget edge now that the outer no longer bleeds left.
  padding-left: 0;
  padding-right: 0;
}

.card-items {
  .showing {
    width: 28rem;
    flex-shrink: 0;
  }

  @include mobile {
    flex-direction: column;

    .showing {
      width: 100%;
    }
  }
}

@include mobile {
  .upcoming-showings-block {
    gap: var(--t-spacing-lg);
  }
}
</style>
